import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, FileText, User, Building2, Calendar, Percent, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getPledges, getPledgeBankSummary, getPledgesByLoan, updateLoanCollateralStatus, updateLoanStatus } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import LoanFilterBar from '../components/LoanFilterBar';

const PAGE_SIZE = 20;

const BANK_ACCENTS = [
    { iconBg: '#dbeafe', iconColor: '#2563eb', badgeBg: '#eff6ff', badgeColor: '#1d4ed8', btnBorder: '#2563eb', btnColor: '#2563eb', countColor: '#2563eb' },
    { iconBg: '#dcfce7', iconColor: '#16a34a', badgeBg: '#f0fdf4', badgeColor: '#15803d', btnBorder: '#16a34a', btnColor: '#16a34a', countColor: '#16a34a' },
    { iconBg: '#ede9fe', iconColor: '#7c3aed', badgeBg: '#f5f3ff', badgeColor: '#6d28d9', btnBorder: '#7c3aed', btnColor: '#7c3aed', countColor: '#7c3aed' },
    { iconBg: '#ffedd5', iconColor: '#ea580c', badgeBg: '#fff7ed', badgeColor: '#c2410c', btnBorder: '#ea580c', btnColor: '#ea580c', countColor: '#ea580c' }
];

const Collateral = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [pledges, setPledges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const MIN_SEARCH_CHARS = 2;
    const searchTermTrimmed = searchTerm.trim();
    const searchTooShort = searchTermTrimmed.length > 0 && searchTermTrimmed.length < MIN_SEARCH_CHARS;
    const [filterStatus, setFilterStatus] = useState(location.state?.filterStatus || 'ALL');
    const [dateFilterMode, setDateFilterMode] = useState('date');
    const [filterDate, setFilterDate] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [bankSummaryOpen, setBankSummaryOpen] = useState(false);
    const [bankSummaryLoading, setBankSummaryLoading] = useState(false);
    const [bankSummaryError, setBankSummaryError] = useState(null);
    const [bankSummary, setBankSummary] = useState(null);
    const [historyModal, setHistoryModal] = useState({
        show: false,
        loading: false,
        loanRow: null,
        pledges: []
    });

    const collateralStatusOptions = ['ALL', 'IN_HAND', 'PLEDGED', 'AWAITING', 'HANDOVER'];

    /** One row per loan — prefer the active (current) pledge when duplicates appear. */
    const groupPledgesByLoan = (list) => {
        const byLoan = new Map();
        (list || []).forEach((pledge) => {
            const key = pledge.loanId || pledge.id;
            const existing = byLoan.get(key);
            if (!existing) {
                byLoan.set(key, pledge);
                return;
            }
            const preferNew = pledge.isActive && !existing.isActive;
            const preferNewerDate = !existing.isActive && !pledge.isActive
                && String(pledge.pledgeDate || '') > String(existing.pledgeDate || '');
            if (preferNew || preferNewerDate) {
                byLoan.set(key, {
                    ...pledge,
                    previousPledgeCount: Math.max(
                        Number(pledge.previousPledgeCount || 0),
                        Number(existing.previousPledgeCount || 0)
                    )
                });
            } else {
                byLoan.set(key, {
                    ...existing,
                    previousPledgeCount: Math.max(
                        Number(existing.previousPledgeCount || 0),
                        Number(pledge.previousPledgeCount || 0)
                    )
                });
            }
        });
        return Array.from(byLoan.values());
    };

    const normalizeStatus = (status) => (status || '').toUpperCase();

    const getStatusOptionLabel = (status) => {
        const labels = {
            ALL: t('collateral.statusOptions.all'),
            IN_HAND: t('collateral.statusOptions.inHand'),
            PLEDGED: t('collateral.statusOptions.pledged'),
            AWAITING: t('collateral.statusOptions.awaiting'),
            HANDOVER: t('collateral.statusOptions.handover')
        };
        return labels[normalizeStatus(status)] || status;
    };

    const formatStatusLabel = (status) => {
        const label = getStatusOptionLabel(status);
        return label || t('collateral.statusOptions.awaiting');
    };

    const [updatingStatus, setUpdatingStatus] = useState(null);
    const [statusModal, setStatusModal] = useState({
        show: false,
        loanId: null,
        previousStatus: '',
        nextStatus: '',
        pledge: null
    });
    const [statusModalLoading, setStatusModalLoading] = useState(false);
    const [interestDueModal, setInterestDueModal] = useState({
        show: false,
        title: '',
        message: '',
        pledge: null
    });
    const loadingRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            const next = searchTerm.trim();
            if (next.length === 0 || next.length >= MIN_SEARCH_CHARS) {
                setDebouncedSearch(next);
                setPage(0);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const fromState = location.state?.filterStatus;
        if (fromState) {
            setFilterStatus(fromState);
            setPage(0);
        }
    }, [location.state?.filterStatus]);

    const buildDateParams = useCallback(() => {
        if (dateFilterMode === 'date' && filterDate) {
            return { date: filterDate };
        }
        if (dateFilterMode === 'range' && (fromDate || toDate)) {
            return { fromDate: fromDate || '', toDate: toDate || '' };
        }
        if (dateFilterMode === 'month' && filterMonth) {
            const [y, m] = filterMonth.split('-');
            return { year: y, month: m };
        }
        if (dateFilterMode === 'year' && filterYear) {
            return { year: filterYear };
        }
        return {};
    }, [dateFilterMode, filterDate, fromDate, toDate, filterMonth, filterYear]);

    const fetchPledges = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        try {
            setLoading(true);
            setError(null);
            const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
            if (!selectedBusiness?.id) {
                setError(t('collateral.errors.noBusinessSelected'));
                setPledges([]);
                setTotalPages(0);
                setTotalElements(0);
                return;
            }

            const result = await getPledges({
                page,
                size: PAGE_SIZE,
                search: debouncedSearch.length >= MIN_SEARCH_CHARS ? debouncedSearch : '',
                collateralStatus: filterStatus,
                ...buildDateParams()
            });

            if (result.success && result.data) {
                const payload = result.data;
                const content = Array.isArray(payload) ? payload : (payload.content || []);
                setPledges(groupPledgesByLoan(content));
                setTotalPages(payload.totalPages || 0);
                setTotalElements(payload.totalElements || content.length);
            } else {
                setError(result.message || t('collateral.errors.loadFailed'));
                setPledges([]);
                setTotalPages(0);
                setTotalElements(0);
            }
        } catch (err) {
            console.error('Error fetching pledges:', err);
            setError(t('collateral.errors.loadFailed'));
            setPledges([]);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [page, debouncedSearch, filterStatus, buildDateParams, t]);

    useEffect(() => {
        fetchPledges();
    }, [fetchPledges]);

    const handleFilterApply = (payload) => {
        setDateFilterMode(payload.dateMode || 'date');
        setFilterDate(payload.filterDate || '');
        setFromDate(payload.fromDate || '');
        setToDate(payload.toDate || '');
        setFilterMonth(payload.filterMonth || '');
        setFilterYear(payload.filterYear || '');
        setFilterStatus(payload.statusFilter || 'ALL');
        setPage(0);
    };

    const handleFilterReset = () => {
        setDateFilterMode('date');
        setFilterDate('');
        setFromDate('');
        setToDate('');
        setFilterMonth('');
        setFilterYear('');
        setFilterStatus('ALL');
        setSearchTerm('');
        setDebouncedSearch('');
        setPage(0);
    };

    const handleStatusChange = async (loanId, newStatus) => {
        const pledgeForPopup = statusModal.pledge;
        setUpdatingStatus(loanId);
        setStatusModalLoading(true);
        try {
            const result = await updateLoanCollateralStatus(loanId, newStatus);
            if (result.success) {
                if (newStatus === 'HANDOVER') {
                    try {
                        await updateLoanStatus(loanId, 'COMPLETED');
                    } catch (loanStatusError) {
                        console.error('Failed to update loan status:', loanStatusError);
                    }
                }
                await fetchPledges();
                setError(null);
            } else {
                const message = result.message || t('collateral.errors.updateFailed');
                const isInterest = /interest is due/i.test(message);
                const isLoanDue = /full due amount is paid/i.test(message);
                if (isInterest || isLoanDue) {
                    setInterestDueModal({
                        show: true,
                        title: isInterest
                            ? (t('collateral.interestDueModal.title') || 'Interest is due')
                            : (t('collateral.interestDueModal.loanDueTitle') || 'Payment required'),
                        message: isInterest
                            ? (message || t('collateral.errors.interestDue'))
                            : (t('collateral.errors.loanDue') || message),
                        pledge: pledgeForPopup
                    });
                    setError(null);
                } else {
                    setError(message);
                }
            }
        } catch (err) {
            console.error('Error updating status:', err);
            setError(t('collateral.errors.updateFailed'));
        } finally {
            // Close confirm popup after API finishes
            setUpdatingStatus(null);
            setStatusModalLoading(false);
            setStatusModal({ show: false, loanId: null, previousStatus: '', nextStatus: '', pledge: null });
        }
    };

    const isStatusLocked = (pledge) => {
        const collateral = normalizeStatus(pledge?.collateralStatus);
        const loanStatus = normalizeStatus(pledge?.loanStatus);
        return collateral === 'HANDOVER'
            || (loanStatus === 'COMPLETED' && collateral === 'HANDOVER');
    };

    const openStatusModal = async (pledge, nextStatus) => {
        if (isStatusLocked(pledge)) return;

        const previousStatus = pledge.collateralStatus || '';
        if (nextStatus === previousStatus) return;

        // Always show confirm first. Due/interest popup only after API fails.
        setError(null);
        setStatusModal({
            show: true,
            loanId: pledge.loanId,
            previousStatus,
            nextStatus,
            pledge
        });
    };

    const closeStatusModal = () => {
        if (statusModalLoading) return;
        setStatusModal({ show: false, loanId: null, previousStatus: '', nextStatus: '', pledge: null });
    };

    const confirmStatusModal = () => {
        if (!statusModal.loanId || !statusModal.nextStatus) return;
        handleStatusChange(statusModal.loanId, statusModal.nextStatus);
    };

    const openHistoryModal = async (loanRow) => {
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        if (!businessId || !loanRow?.loanId) return;

        setHistoryModal({ show: true, loading: true, loanRow, pledges: [] });
        try {
            const result = await getPledgesByLoan(businessId, loanRow.loanId);
            const list = result.success && Array.isArray(result.data) ? result.data : [];
            const sorted = [...list].sort((a, b) => {
                const activeScore = (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0);
                if (activeScore !== 0) return activeScore;
                return String(b.pledgeDate || '').localeCompare(String(a.pledgeDate || ''));
            });
            setHistoryModal({ show: true, loading: false, loanRow, pledges: sorted });
        } catch (err) {
            console.error('Error loading pledge history:', err);
            setHistoryModal({ show: true, loading: false, loanRow, pledges: [] });
        }
    };

    const closeHistoryModal = () => {
        setHistoryModal({ show: false, loading: false, loanRow: null, pledges: [] });
    };

    const formatHistoryEnd = (item) => {
        if (item.isActive) return t('collateral.history.present') || 'Present';
        if (item.interestFreezeDate) return formatDate(item.interestFreezeDate);
        return formatDate(item.updatedAt);
    };

    const previousCountFor = (pledge) => {
        const count = Number(pledge.previousPledgeCount || 0);
        return count > 0 ? count : 0;
    };

    const loadBankSummary = useCallback(async () => {
        setBankSummaryLoading(true);
        setBankSummaryError(null);
        try {
            const result = await getPledgeBankSummary();
            if (result.success && result.data) {
                setBankSummary(result.data);
            } else {
                setBankSummary(null);
                setBankSummaryError(result.message || t('collateral.bankSummary.loadError'));
            }
        } catch (err) {
            console.error('Error loading bank summary:', err);
            setBankSummary(null);
            setBankSummaryError(t('collateral.bankSummary.loadError'));
        } finally {
            setBankSummaryLoading(false);
        }
    }, [t]);

    const openBankSummary = () => {
        setBankSummaryOpen(true);
        loadBankSummary();
    };

    const closeBankSummary = () => {
        setBankSummaryOpen(false);
    };

    const viewBankLoans = (bankName) => {
        setBankSummaryOpen(false);
        setSearchTerm(bankName || '');
        setDebouncedSearch((bankName || '').trim());
        setPage(0);
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatCurrencyExact = (amount) => {
        const value = Number(amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('loans.labels.na');
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    const formatLoanId = (id, loanNumber) => loanNumber || t('loans.labels.na');

    const getStatusBgColor = (status) => {
        switch (status) {
            case 'IN_HAND': return '#dcfce7';
            case 'PLEDGED': return '#fef3c7';
            case 'AWAITING': return '#fee2e2';
            case 'HANDOVER': return '#e0e7ff';
            default: return '#f3f4f6';
        }
    };

    const getStatusTextColor = (status) => {
        switch (status) {
            case 'IN_HAND': return '#166534';
            case 'PLEDGED': return '#92400e';
            case 'AWAITING': return '#991b1b';
            case 'HANDOVER': return '#3730a3';
            default: return '#6b7280';
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        return (
            <div className="list-pagination">
                <button
                    type="button"
                    className="btn btn-outline list-pagination-btn"
                    disabled={page <= 0 || loading}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                    <ChevronLeft size={16} />
                    {t('common.prev')}
                </button>
                <span className="list-pagination-info">
                    {t('common.page')} {page + 1} / {Math.max(totalPages, 1)}
                    <span className="list-pagination-total">
                        ({totalElements} {t('common.total')})
                    </span>
                </span>
                <button
                    type="button"
                    className="btn btn-outline list-pagination-btn"
                    disabled={page >= totalPages - 1 || loading}
                    onClick={() => setPage((p) => p + 1)}
                >
                    {t('common.next')}
                    <ChevronRight size={16} />
                </button>
            </div>
        );
    };

    return (
        <>
        <div>
            <div className="card list-page-card">
                <div className="card-header list-page-header">
                    <div className="list-filters">
                        <div className="list-filter-search">
                            <div className="list-filter-search-control">
                                <Search size={18} className="list-filter-search-icon" />
                                <input
                                    type="text"
                                    className={`search-input${searchTooShort ? ' search-input--error' : ''}`}
                                    placeholder={t('collateral.searchPlaceholderDetailed')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="list-filter-search-clear"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setDebouncedSearch('');
                                            setPage(0);
                                        }}
                                        aria-label="Clear search"
                                        title="Clear"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            {searchTooShort && (
                                <p className="list-filter-search-error">{t('loans.messages.searchMinChars')}</p>
                            )}
                        </div>
                        <div className="loan-filter-toolbar">
                            <LoanFilterBar
                                t={t}
                                language={language}
                                statusOptions={collateralStatusOptions}
                                getStatusOptionLabel={getStatusOptionLabel}
                                dateMode={dateFilterMode}
                                filterDate={filterDate}
                                fromDate={fromDate}
                                toDate={toDate}
                                filterMonth={filterMonth}
                                filterYear={filterYear}
                                statusFilter={filterStatus}
                                onApply={handleFilterApply}
                                onReset={handleFilterReset}
                            />
                            <div className="list-filter-cta-group">
                                <button
                                    type="button"
                                    className="btn btn-outline list-filter-cta"
                                    onClick={openBankSummary}
                                >
                                    <Building2 size={16} />
                                    {t('collateral.bankSummary.button')}
                                </button>
                                <Link to="/collateral/create" className="btn btn-primary list-filter-cta">
                                    <Plus size={16} />
                                    {t('collateral.createNewPledge')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div style={{
                        padding: '1rem',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem',
                        color: '#991b1b'
                    }}>
                        {error}
                    </div>
                )}

                <div className="table-container desktop-table-only">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>{t('collateral.tableHeaders.loanId')}</th>
                                <th>{t('loans.tableHeaders.serialNumber')}</th>
                                <th>{t('collateral.tableHeaders.borrower')}</th>
                                <th>{t('collateral.tableHeaders.pledgedTo')}</th>
                                <th>{t('collateral.tableHeaders.pledgeAmount')}</th>
                                <th>{t('collateral.tableHeaders.interestRate')}</th>
                                <th>{t('collateral.tableHeaders.pledgeDate')}</th>
                                <th>{t('collateral.tableHeaders.maturityDate')}</th>
                                <th>{t('collateral.tableHeaders.status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                                        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
                                        <p>{t('collateral.loadingMessage')}</p>
                                    </td>
                                </tr>
                            ) : pledges.length === 0 ? (
                                <tr>
                                    <td colSpan="9" style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                                        {t('collateral.emptyState')}
                                    </td>
                                </tr>
                            ) : (
                                pledges.map((pledge) => (
                                    <tr
                                        key={pledge.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                            if (e.target.tagName !== 'SELECT' && e.target.tagName !== 'OPTION') {
                                                navigate(`/pledges/${pledge.id}`, { state: { pledge } });
                                            }
                                        }}
                                    >
                                        <td style={{ color: '#3b82f6', fontWeight: '500' }}>
                                            {formatLoanId(pledge.loanId, pledge.loanNumber)}
                                        </td>
                                        <td style={{
                                            color: pledge.serialNumber ? '#1d4ed8' : '#9ca3af',
                                            fontWeight: pledge.serialNumber ? 700 : 400,
                                            fontStyle: pledge.serialNumber ? 'normal' : 'italic',
                                            textTransform: pledge.serialNumber ? 'uppercase' : 'none'
                                        }}>
                                            {pledge.serialNumber || t('loans.labels.serialMissing')}
                                        </td>
                                        <td>{pledge.borrowerName || t('loans.labels.na')}</td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className="pledge-bank-cell">
                                                <span>{pledge.pledgedTo || t('loans.labels.na')}</span>
                                                {previousCountFor(pledge) > 0 && (
                                                    <button
                                                        type="button"
                                                        className="pledge-history-chip"
                                                        onClick={() => openHistoryModal(pledge)}
                                                    >
                                                        +{previousCountFor(pledge)} {t('collateral.history.previous') || 'previous'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ fontWeight: '500' }}>{formatCurrency(pledge.pledgeAmount)}</td>
                                        <td>{pledge.interestRate}%</td>
                                        <td>{formatDate(pledge.pledgeDate)}</td>
                                        <td>{formatDate(pledge.maturityDate)}</td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <select
                                                value={pledge.collateralStatus || ''}
                                                onChange={(e) => {
                                                    const previousStatus = pledge.collateralStatus || '';
                                                    const nextStatus = e.target.value;
                                                    if (nextStatus === previousStatus) return;
                                                    e.target.value = previousStatus;
                                                    openStatusModal(pledge, nextStatus);
                                                }}
                                                disabled={updatingStatus === pledge.loanId || isStatusLocked(pledge)}
                                                title={isStatusLocked(pledge)
                                                    ? (t('collateral.errors.statusLocked') || 'Completed / handed over — status locked')
                                                    : undefined}
                                                style={{
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '0.25rem',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    backgroundColor: getStatusBgColor(pledge.collateralStatus),
                                                    color: getStatusTextColor(pledge.collateralStatus),
                                                    border: 'none',
                                                    cursor: (updatingStatus === pledge.loanId || isStatusLocked(pledge)) ? 'not-allowed' : 'pointer',
                                                    opacity: (updatingStatus === pledge.loanId || isStatusLocked(pledge)) ? 0.6 : 1
                                                }}
                                            >
                                                <option value="IN_HAND">{getStatusOptionLabel('IN_HAND')}</option>
                                                <option value="PLEDGED">{getStatusOptionLabel('PLEDGED')}</option>
                                                <option value="AWAITING">{getStatusOptionLabel('AWAITING')}</option>
                                                <option value="HANDOVER">{getStatusOptionLabel('HANDOVER')}</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mobile-card-list">
                    {loading ? (
                        <div className="mobile-card-empty">
                            <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                            <span>{t('collateral.loadingMessage')}</span>
                        </div>
                    ) : pledges.length === 0 ? (
                        <div className="mobile-card-empty">{t('collateral.emptyState')}</div>
                    ) : (
                        pledges.map((pledge) => {
                            const status = pledge.collateralStatus || '';
                            const statusClass =
                                status === 'IN_HAND' ? 'is-active' :
                                status === 'PLEDGED' ? 'is-pending' :
                                status === 'HANDOVER' ? 'is-completed' : 'is-pending';

                            return (
                                <article
                                    key={pledge.id}
                                    className="loan-mobile-card"
                                    onClick={() => navigate(`/pledges/${pledge.id}`, { state: { pledge } })}
                                >
                                    <div className="loan-card-top">
                                        <div className="loan-card-id">
                                            <span className="loan-card-icon"><FileText size={16} /></span>
                                            <div className="loan-card-id-stack">
                                                <span className="loan-card-id-text">{formatLoanId(pledge.loanId, pledge.loanNumber)}</span>
                                                {pledge.serialNumber && (
                                                    <span className="loan-card-serial">{String(pledge.serialNumber).toUpperCase()}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="loan-card-top-actions">
                                            <span className={`loan-status-pill ${statusClass}`}>
                                                <span className="loan-status-dot" />
                                                {formatStatusLabel(status)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="loan-card-body">
                                        <div className="loan-card-customer">
                                            <div className="loan-card-meta-row">
                                                <User size={15} />
                                                <span>{pledge.borrowerName || t('loans.labels.na')}</span>
                                            </div>
                                            <div className="loan-card-meta-row">
                                                <Building2 size={15} />
                                                <span>{pledge.pledgedTo || t('loans.labels.na')}</span>
                                            </div>
                                            {previousCountFor(pledge) > 0 && (
                                                <button
                                                    type="button"
                                                    className="pledge-history-chip"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openHistoryModal(pledge);
                                                    }}
                                                >
                                                    +{previousCountFor(pledge)} {t('collateral.history.previous') || 'previous'}
                                                </button>
                                            )}
                                        </div>
                                        <div className="loan-card-principal">
                                            <span className="loan-card-principal-label">{t('collateral.tableHeaders.pledgeAmount')}</span>
                                            <span className="loan-card-principal-value">{formatCurrency(pledge.pledgeAmount)}</span>
                                        </div>
                                    </div>

                                    <div className="loan-card-footer">
                                        <div className="loan-card-footer-item">
                                            <Percent size={15} />
                                            <div>
                                                <span className="loan-card-footer-label">{t('collateral.tableHeaders.interestRate')}</span>
                                                <span className="loan-card-footer-value">{pledge.interestRate}%</span>
                                            </div>
                                        </div>
                                        <div className="loan-card-footer-item">
                                            <Calendar size={15} />
                                            <div>
                                                <span className="loan-card-footer-label">{t('collateral.tableHeaders.pledgeDate')}</span>
                                                <span className="loan-card-footer-value">{formatDate(pledge.pledgeDate)}</span>
                                            </div>
                                        </div>
                                        <div className="loan-card-footer-item" onClick={(e) => e.stopPropagation()}>
                                            <select
                                                value={pledge.collateralStatus || ''}
                                                onChange={(e) => {
                                                    const previousStatus = pledge.collateralStatus || '';
                                                    const nextStatus = e.target.value;
                                                    if (nextStatus === previousStatus) return;
                                                    e.target.value = previousStatus;
                                                    openStatusModal(pledge, nextStatus);
                                                }}
                                                disabled={updatingStatus === pledge.loanId || isStatusLocked(pledge)}
                                                title={isStatusLocked(pledge)
                                                    ? (t('collateral.errors.statusLocked') || 'Completed / handed over — status locked')
                                                    : undefined}
                                                className="form-select"
                                                style={{
                                                    minWidth: '0',
                                                    width: '100%',
                                                    height: '36px',
                                                    fontSize: '0.75rem',
                                                    padding: '0.25rem 1.75rem 0.25rem 0.5rem',
                                                    backgroundColor: getStatusBgColor(pledge.collateralStatus),
                                                    color: getStatusTextColor(pledge.collateralStatus),
                                                    border: 'none',
                                                    fontWeight: 600,
                                                    cursor: (updatingStatus === pledge.loanId || isStatusLocked(pledge)) ? 'not-allowed' : 'pointer',
                                                    opacity: (updatingStatus === pledge.loanId || isStatusLocked(pledge)) ? 0.6 : 1
                                                }}
                                            >
                                                <option value="IN_HAND">{getStatusOptionLabel('IN_HAND')}</option>
                                                <option value="PLEDGED">{getStatusOptionLabel('PLEDGED')}</option>
                                                <option value="AWAITING">{getStatusOptionLabel('AWAITING')}</option>
                                                <option value="HANDOVER">{getStatusOptionLabel('HANDOVER')}</option>
                                            </select>
                                        </div>
                                    </div>
                                </article>
                            );
                        })
                    )}
                </div>

                {renderPagination()}
            </div>
        </div>

        {statusModal.show && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'var(--bg-surface)',
                    padding: '2rem',
                    borderRadius: '12px',
                    minWidth: '0', width: 'min(360px, 95vw)',
                    maxWidth: '90vw',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                    border: '1px solid var(--border-color)'
                }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>{t('collateral.statusModal.title')}</h3>
                    <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {t('collateral.statusModal.messageStart')}{' '}
                        <strong>{statusModal.pledge?.borrowerName || t('collateral.statusModal.defaultName')}</strong>{' '}
                        {t('collateral.statusModal.from')}{' '}
                        <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            backgroundColor: getStatusBgColor(statusModal.previousStatus),
                            color: getStatusTextColor(statusModal.previousStatus),
                            margin: '0 0.35rem',
                            fontWeight: 600,
                            fontSize: '0.85rem'
                        }}>{formatStatusLabel(statusModal.previousStatus)}</span>{' '}
                        {t('collateral.statusModal.to')}{' '}
                        <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            backgroundColor: getStatusBgColor(statusModal.nextStatus),
                            color: getStatusTextColor(statusModal.nextStatus),
                            margin: '0 0.35rem',
                            fontWeight: 600,
                            fontSize: '0.85rem'
                        }}>{formatStatusLabel(statusModal.nextStatus)}</span>
                        ?
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button
                            className="btn btn-outline"
                            onClick={closeStatusModal}
                            style={{ padding: '0.5rem 1rem' }}
                            disabled={statusModalLoading}
                        >
                            {t('collateral.statusModal.cancel')}
                        </button>
                        <button
                            className="btn"
                            onClick={confirmStatusModal}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                            disabled={statusModalLoading}
                        >
                            {statusModalLoading ? (
                                <>
                                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                    {t('collateral.statusModal.updating')}
                                </>
                            ) : (
                                t('collateral.statusModal.confirm')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {interestDueModal.show && (
            <div
                className="pledge-interest-due-overlay"
                onClick={() => setInterestDueModal({ show: false, title: '', message: '', pledge: null })}
            >
                <div
                    className="pledge-interest-due-modal"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    <h3 className="pledge-interest-due-title">
                        {interestDueModal.title
                            || t('collateral.interestDueModal.title')
                            || 'Interest is due'}
                    </h3>
                    <p className="pledge-interest-due-message">
                        {interestDueModal.message
                            || t('collateral.errors.interestDue')
                            || 'Interest is due. Pay pledge interest before changing status to In Hand.'}
                    </p>
                    <div className="pledge-interest-due-actions">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                const pledge = interestDueModal.pledge;
                                setInterestDueModal({ show: false, title: '', message: '', pledge: null });
                                if (pledge?.id) {
                                    navigate(`/pledges/${pledge.id}`, { state: { pledge } });
                                }
                            }}
                        >
                            {t('collateral.interestDueModal.payNow') || 'Pay Interest'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => setInterestDueModal({ show: false, title: '', message: '', pledge: null })}
                        >
                            {t('collateral.interestDueModal.ok') || 'OK'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {bankSummaryOpen && (
            <div className="bank-summary-overlay" onClick={closeBankSummary}>
                <div className="bank-summary-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="bank-summary-header">
                        <div>
                            <h3 className="bank-summary-title">{t('collateral.bankSummary.title')}</h3>
                            <p className="bank-summary-subtitle">{t('collateral.bankSummary.subtitle')}</p>
                        </div>
                        <button type="button" className="bank-summary-close" onClick={closeBankSummary} aria-label="Close">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="bank-summary-body">
                        {bankSummaryLoading ? (
                            <div className="bank-summary-loading">
                                <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite' }} />
                                <span>{t('collateral.bankSummary.loading')}</span>
                            </div>
                        ) : bankSummaryError ? (
                            <div className="bank-summary-error">
                                <p>{bankSummaryError}</p>
                                <button type="button" className="btn btn-outline" onClick={loadBankSummary}>
                                    {t('collateral.bankSummary.retry')}
                                </button>
                            </div>
                        ) : !bankSummary?.banks?.length ? (
                            <div className="bank-summary-empty">
                                <Building2 size={36} style={{ color: '#9ca3af' }} />
                                <strong>{t('collateral.bankSummary.emptyTitle')}</strong>
                                <span>{t('collateral.bankSummary.emptyMessage')}</span>
                            </div>
                        ) : (
                            <>
                                {(bankSummary.total || bankSummary.allTimeTotal) && (
                                    <div className="bank-summary-rollup-row">
                                        {bankSummary.total && (
                                            <div className="bank-summary-card bank-summary-rollup bank-summary-rollup-active">
                                                <div className="bank-summary-card-top">
                                                    <div className="bank-summary-card-identity">
                                                        <span className="bank-summary-bank-icon bank-summary-rollup-icon">
                                                            Σ
                                                        </span>
                                                        <div>
                                                            <div className="bank-summary-bank-name">{t('collateral.bankSummary.activeTotalSummary')}</div>
                                                            <span className="bank-summary-rate-badge bank-summary-rollup-hint">
                                                                {t('collateral.bankSummary.activeTotalHint')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span className="bank-summary-loans-pill">
                                                        <Calendar size={14} />
                                                        {(t('collateral.bankSummary.loansBadge') || '{count} Loans')
                                                            .replace('{count}', String(bankSummary.total.loanCount ?? 0))}
                                                    </span>
                                                </div>
                                                <div className="bank-summary-metrics">
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalLoans')}</span>
                                                        <span className="bank-summary-metric-value bank-summary-rollup-accent">
                                                            {bankSummary.total.loanCount}
                                                        </span>
                                                    </div>
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalPledgedAmount')}</span>
                                                        <span className="bank-summary-metric-value bank-summary-rollup-accent">
                                                            {formatCurrencyExact(bankSummary.total.pledgedAmount)}
                                                        </span>
                                                    </div>
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalInterestPaid')}</span>
                                                        <span className="bank-summary-metric-value" style={{ color: '#16a34a' }}>
                                                            {formatCurrencyExact(bankSummary.total.interestPaid)}
                                                        </span>
                                                    </div>
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalInterestDue')}</span>
                                                        <span className="bank-summary-metric-value" style={{ color: '#ea580c' }}>
                                                            {formatCurrencyExact(bankSummary.total.interestOutstanding)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {bankSummary.allTimeTotal && (
                                            <div className="bank-summary-card bank-summary-rollup bank-summary-rollup-alltime">
                                                <div className="bank-summary-card-top">
                                                    <div className="bank-summary-card-identity">
                                                        <span className="bank-summary-bank-icon bank-summary-rollup-icon">
                                                            Σ
                                                        </span>
                                                        <div>
                                                            <div className="bank-summary-bank-name">{t('collateral.bankSummary.allTimeTotalSummary')}</div>
                                                            <span className="bank-summary-rate-badge bank-summary-rollup-hint">
                                                                {t('collateral.bankSummary.allTimeTotalHint')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span className="bank-summary-loans-pill">
                                                        <Calendar size={14} />
                                                        {(t('collateral.bankSummary.loansBadge') || '{count} Loans')
                                                            .replace('{count}', String(bankSummary.allTimeTotal.loanCount ?? 0))}
                                                    </span>
                                                </div>
                                                <div className="bank-summary-metrics">
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalLoans')}</span>
                                                        <span className="bank-summary-metric-value bank-summary-rollup-accent">
                                                            {bankSummary.allTimeTotal.loanCount}
                                                        </span>
                                                    </div>
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalPledgedAmount')}</span>
                                                        <span className="bank-summary-metric-value bank-summary-rollup-accent">
                                                            {formatCurrencyExact(bankSummary.allTimeTotal.pledgedAmount)}
                                                        </span>
                                                    </div>
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalInterestPaid')}</span>
                                                        <span className="bank-summary-metric-value" style={{ color: '#16a34a' }}>
                                                            {formatCurrencyExact(bankSummary.allTimeTotal.interestPaid)}
                                                        </span>
                                                    </div>
                                                    <div className="bank-summary-metric">
                                                        <span className="bank-summary-metric-label">{t('collateral.bankSummary.totalInterestDue')}</span>
                                                        <span className="bank-summary-metric-value" style={{ color: '#ea580c' }}>
                                                            {formatCurrencyExact(bankSummary.allTimeTotal.interestOutstanding)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {bankSummary.banks.map((bank, index) => {
                                    const accent = BANK_ACCENTS[index % BANK_ACCENTS.length];
                                    const viewLabel = (t('collateral.bankSummary.viewLoans') || 'View {count} Loans →')
                                        .replace('{count}', String(bank.loanCount));
                                    return (
                                        <div key={bank.bankName} className="bank-summary-card">
                                            <div className="bank-summary-card-top">
                                                <div className="bank-summary-card-identity">
                                                    <span
                                                        className="bank-summary-bank-icon"
                                                        style={{ background: accent.iconBg, color: accent.iconColor }}
                                                    >
                                                        <Building2 size={18} />
                                                    </span>
                                                    <div className="bank-summary-bank-meta">
                                                        <div className="bank-summary-bank-name" title={bank.bankName}>
                                                            {bank.bankName}
                                                        </div>
                                                        <span
                                                            className="bank-summary-rate-badge"
                                                            style={{ background: accent.badgeBg, color: accent.badgeColor }}
                                                        >
                                                            {bank.interestRate}% {t('collateral.bankSummary.perMonth')}
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline bank-summary-view-btn"
                                                    style={{ borderColor: accent.btnBorder, color: accent.btnColor }}
                                                    onClick={() => viewBankLoans(bank.bankName)}
                                                    title={viewLabel}
                                                >
                                                    {viewLabel}
                                                </button>
                                            </div>
                                            <div className="bank-summary-metrics">
                                                <div className="bank-summary-metric">
                                                    <span className="bank-summary-metric-label">{t('collateral.bankSummary.pledgedLoans')}</span>
                                                    <span className="bank-summary-metric-value" style={{ color: accent.countColor }}>
                                                        {bank.loanCount}
                                                    </span>
                                                </div>
                                                <div className="bank-summary-metric">
                                                    <span className="bank-summary-metric-label">{t('collateral.bankSummary.pledgedAmount')}</span>
                                                    <span className="bank-summary-metric-value">{formatCurrencyExact(bank.pledgedAmount)}</span>
                                                </div>
                                                <div className="bank-summary-metric">
                                                    <span className="bank-summary-metric-label">{t('collateral.bankSummary.interestPaid')}</span>
                                                    <span className="bank-summary-metric-value" style={{ color: '#16a34a' }}>
                                                        {formatCurrencyExact(bank.interestPaid)}
                                                    </span>
                                                </div>
                                                <div className="bank-summary-metric">
                                                    <span className="bank-summary-metric-label">{t('collateral.bankSummary.interestDue')}</span>
                                                    <span className="bank-summary-metric-value" style={{ color: '#ea580c' }}>
                                                        {formatCurrencyExact(bank.interestOutstanding)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            </div>
        )}

        {historyModal.show && (
            <div className="pledge-history-overlay" onClick={closeHistoryModal}>
                <div className="pledge-history-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="pledge-history-header">
                        <div>
                            <h3 className="pledge-history-title">{t('collateral.history.title') || 'Pledge History'}</h3>
                            <p className="pledge-history-subtitle">
                                {t('collateral.tableHeaders.loanNo') || 'Loan No.'}{' '}
                                {historyModal.loanRow?.loanNumber
                                    || (historyModal.loanRow?.loanId
                                        ? String(historyModal.loanRow.loanId).substring(0, 8).toUpperCase()
                                        : '—')}
                                {historyModal.loanRow?.serialNumber
                                    ? ` • ${t('collateral.tableHeaders.serialNo') || 'Serial No.'} ${String(historyModal.loanRow.serialNumber).toUpperCase()}`
                                    : ''}
                            </p>
                        </div>
                        <button type="button" className="pledge-history-close" onClick={closeHistoryModal} aria-label="Close">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="pledge-history-body">
                        {historyModal.loading ? (
                            <div className="pledge-history-loading">
                                <RefreshCw size={22} style={{ animation: 'spin 1s linear infinite' }} />
                            </div>
                        ) : historyModal.pledges.length === 0 ? (
                            <p className="pledge-history-empty">{t('collateral.emptyState')}</p>
                        ) : (
                            <ul className="pledge-history-timeline">
                                {historyModal.pledges.map((item, index) => {
                                    const isCurrent = Boolean(item.isActive);
                                    const label = isCurrent
                                        ? (t('collateral.history.current') || 'CURRENT')
                                        : (index === 1
                                            ? (t('collateral.history.previousLabel') || 'PREVIOUS')
                                            : (t('collateral.history.older') || 'OLDER'));
                                    return (
                                        <li
                                            key={item.id}
                                            className={`pledge-history-item ${isCurrent ? 'is-current' : 'is-previous'}`}
                                            onClick={() => {
                                                closeHistoryModal();
                                                navigate(`/pledges/${item.id}`, { state: { pledge: item } });
                                            }}
                                        >
                                            <span className="pledge-history-dot" />
                                            <div className="pledge-history-card">
                                                <div className="pledge-history-card-top">
                                                    <strong>{item.pledgedTo || t('loans.labels.na')}</strong>
                                                    <span className={`pledge-history-badge ${isCurrent ? 'current' : 'previous'}`}>
                                                        {label}
                                                    </span>
                                                </div>
                                                <div className="pledge-history-meta">
                                                    {formatCurrency(item.pledgeAmount)} · {item.interestRate}% / month
                                                </div>
                                                <div className="pledge-history-dates">
                                                    {formatDate(item.pledgeDate)} → {formatHistoryEnd(item)}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default Collateral;
